import { Component, Suspense, lazy } from "react";
import t from "prop-types";
import { create } from "jss";
import rtl from "jss-rtl";
import { withStyles, StylesProvider, jssPreset } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { BrowserRouter as Router, Route, Switch, withRouter, Redirect } from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import { loadUser, logout, verifyVersion } from "../routes/auth/actions";
import { loadClinics } from "../routes/clinics/actions";
// import Header from "../routes/components/header/index";
import { currentUser as currentUserSelector } from "../routes/auth/selectors";
import HomeRoutes from "../routes/home/routes/index";
import TreatmentRoutes from "../routes/treatment/routes";
import { ROLES } from "@app/constants";
import UsersRoutes from "../routes/users/routes";
import ResourcesRoutes from "../routes/resources/routes";
import LoginPage from "../routes/auth/containers/login";
import LoginWithOauth from "../routes/auth/containers/login-with-provider";
import NotificationCenter from "../routes/components/notification-center";
import PasswordPage from "../routes/auth/containers/password";
import PasswordReset from "../routes/auth/containers/password-reset";
import * as Sentry from "@sentry/browser";
// import localeData from "@app/i18n/data.json";
import { initAnalytics, setUser, setSuperProperties, trackEvent, setUserProperty } from "@amplio-common/analytics";
import { /*Languages,*/ GetLanuageFromId } from "@app/common/data/Languages";
import UserProvider from "./AppContext";
// import OTTest from "@app/OTTest/OTTest";
import { validateUserBrowsingData } from "@amplio-common/funcs/commonFunctionalities";
import CommonDialog from "@app/common/components/material_ui/Dialog";
import ErrorBoundary from "./ErrorBoundary";
import { setUserNetworkDeviceDetails } from "@amplio-common/funcs/performanceTests";
import BrowserSupportNotification from "@app/common/components/BrowserSupportNotification";
import FullScreenLoading from "@app/common/components/FullScreenLoad";
import LoginBg from "@amplio-common/assets/svgs/baloon_no_bg.svg";
import { selectIsFullScreen } from "@app/routes/full_screen/store/fullScreenSlice";
import LeftSideNavigation from "@app/routes/components/sidebar-navigation/LeftSideNavigation";
import Header from "../routes/components/header/index";
import { Trans } from "@lingui/macro";
import moment from "moment-timezone";

//Intercom
import { install as installResizeObserverPolyfill } from "resize-observer";

// import { i18n } from "@lingui/core";
import NewVersionCheck from "./NewVersionCheck";
import IdleTimer from "@app/common/funcs/idleTimer";

const TreatmentComponent = lazy(() => import("@app/routes/treatmentV3/components/Treatment"));

const inactiveTimeout = 7200; //seconds
const warningTimeout = 60; //seconds

const propTypes = {
  history: t.object,
  loadUser: t.func,
  logout: t.func,
  verifyVersion: t.func,
  currentUser: t.object,
  clinics: t.oneOfType([t.object, t.array]),
  classes: t.object,
  isFullScreen: t.bool,
};

const styles = theme => ({
  root: {
    width: "100%",
    height: "100%",
    zIndex: 1,
    overflow: "auto",
    overflowY: "hidden",
    // fontFamily: "roboto, arial",
  },
  appFrame: {
    position: "relative",
    display: "flex",
    height: "100%",
  },
  logo: {
    position: "absolute",
    left: 0,
  },
  content: {
    overflow: "auto",
    width: "100%",
    flexGrow: 1,
    display: "flex",
    backgroundColor: "eaeaea",
    padding: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    height: "100%",
    [theme.breakpoints.up("sm")]: {
      content: {
        height: "100%",
      },
    },
  },
  topNav: {
    height: "calc(100% - 60px)",
    marginTop: 60,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  contentWrapper: {
    height: "100%",
    width: "100%",
    display: "inline-grid",
  },
});

const ErrorRaise = () => {
  throw new Error("Required");
};

class App extends Component {
  constructor(props) {
    super(props);

    const language = (navigator.languages && navigator.languages[0]) || navigator.language || navigator.userLanguage;
    const languageWithoutRegionCode = language.toLowerCase().split(/[_-]+/)[0];

    // Configure JSS
    const rtlPluginArray = [rtl()];
    const jss = create({ plugins: [...jssPreset().plugins, ...rtlPluginArray] });
    this.state = {
      //anchorEl: null,
      //language,
      showBwAlert: false,
      showInactiveAlert: false,
      languageWithoutRegionCode,
      // messages: localeData[languageWithoutRegionCode] || localeData[language] || localeData.en,

      language: languageWithoutRegionCode,

      // eslint-disable-next-line
      allowToSetLanguage: true,
      // eslint-disable-next-line
      setAllowToSetLanguage: (val) => { this.setState({ allowToSetLanguage: val }); },
      isError: false,
      jss,
      warningDuration: warningTimeout,
    };

    const pathName = props.history.location.pathname;
    if (pathName.includes("/reset/confirm/") || pathName.includes("/code/") || pathName.includes("/class_code/")) {
      localStorage.removeItem("code_login_token");
      localStorage.removeItem("class_login_token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("totp_token");
    }

    initAnalytics();
  }

  componentDidMount() {
    if (localStorage.getItem("access_token") || localStorage.getItem("code_login_token") || localStorage.getItem("class_login_token")) {
      this.props.loadUser();
      this.initIdleTimer();
      // this.props.loadClinics();
    }

    //polyfill resize-observer for older browsers (mostly student ipads. we use reisize-observer for studio fullscreen)
    if (!window.ResizeObserver) { installResizeObserverPolyfill(); }
  }

  componentWillUnmount() {
    this.timer && this.timer.cleanUp();
    clearInterval(this.inactiveTimer);
  }

  componentDidUpdate(prevProps) {
    const { currentUser } = this.props;
    const userLoggedIn = prevProps.currentUser !== currentUser && currentUser.id;

    if (userLoggedIn) {
      this.onLoginSuccess(currentUser);
      setUser(userLoggedIn);
      setUserProperty({ name: currentUser.name, email: currentUser.email });
      Sentry.setUser({ id: currentUser.id });

      setSuperProperties({
        language: GetLanuageFromId("he-IL").label,
        user_role: currentUser.role,
      });
      if (!this.timer) this.initIdleTimer();

      //if ([ROLES.THERAPIST.id, ROLES.TRAINER.id].includes(currentUser.role)) {
      // if (!this.isOlarkInitiated) {
      //   this.initOlark();
      // }
      //}
    }

    if (currentUser.id) {
      if (currentUser.role === ROLES.STUDENT.id) {
        this.onLocaleChange("he-IL");
      }
      if (currentUser.role === ROLES.SYSTEM_ADMIN) {
        this.onLocaleChange("en-US");
      }
    }
  }

  initIdleTimer() {
    this.timer = new IdleTimer({
      timeout: inactiveTimeout,
      warningTimeout,
      onWarning: () => {
        this.setState({ showInactiveAlert: true, warningAlert: true }, () => {
          this.warningInterval = setInterval(() => {
            this.setState(prevState => ({
              ...prevState,
              warningDuration: prevState.warningDuration - 1,
            }));
          }, 1000);
        });
      },
      onReset: () => {
        clearInterval(this.warningInterval);
        this.setState({
          showInactiveAlert: false,
          warningAlert: false,
          warningDuration: warningTimeout,
        });
      },
      onTimeout: () => {
        const idleTimeStarted = new Date() - (inactiveTimeout * 1000);
        this.handleInactiveTimer(idleTimeStarted);
        clearInterval(this.warningInterval);
        localStorage.clear();
        window.location.reload();
      },
    });
  }

  handleInactiveTimer(expiredTime) {
    this.inactiveTimer = setInterval(() => {
      this.setState({ inactiveTime: moment(moment()).diff(moment(expiredTime, "x")) });
    }, 1000);
  }

  onLoginSuccess = (currentUser) => {
    const loginRequest = localStorage.getItem("loginRequest");
    const isValid = validateUserBrowsingData();

    // TODO - artem clean this
    if (loginRequest) {
      localStorage.removeItem("loginRequest");
      setUserNetworkDeviceDetails(currentUser).then((res) => {
        if (!res) {
          this.setState({ showBwAlert: true });
          trackEvent("bandwith_popup");
        }
      });
      if (!isValid) {
        trackEvent("notification_after_login");
      }
    }
  }

  onLocaleChange = language => {
    if (this.state.language === language) { return; }
    // Configure JSS
    const rtlPluginArray = language.includes("he") ? [rtl()] : [];
    const jss = create({ plugins: [...jssPreset().plugins, ...rtlPluginArray] });
    const languageWithoutRegionCode = language.toLowerCase().split(/[_-]+/)[0];

    if (languageWithoutRegionCode === this.state.language) {
      return;
    }

    if (this.state.language === languageWithoutRegionCode) { return; }
    this.setState({
      languageWithoutRegionCode,
      language,
      // messages: localeData[languageWithoutRegionCode] || localeData[language] || localeData.en,
      jss,
    });
    // i18n.locale !== languageWithoutRegionCode && i18n.activate(languageWithoutRegionCode);
  };

  // initOlark = () => {
  //   this.isOlarkInitiated = true;
  //   window.showOlark(
  //     this.props.currentUser.email,
  //     this.props.currentUser.name,
  //     this.props.currentUser.phone,
  //   );
  // };
  getIsNoToken = () => {
    const { currentUser } = this.props;
    if (currentUser.isAuthenticated && !currentUser.isVerified) {
      return !localStorage.getItem("class_login_token") && !localStorage.getItem("code_login_token") && !localStorage.getItem("totp_token");
    }
    if (currentUser.isAuthenticated && currentUser.isVerified) {
      return false;
    }
    return true;
  }


  render() {
    const { classes, currentUser, isFullScreen, clinics } = this.props;
    const { languageWithoutRegionCode, showBwAlert, isError, jss, language, showInactiveAlert, warningDuration } = this.state;
    const isNoToken = this.getIsNoToken();
    const isLeftSideNavigation = [ROLES.STUDENT.id, ROLES.THERAPIST.id, ROLES.CLINIC_ADMINISTRATOR.id].includes(currentUser.role);

    const warningConfirmation = () => {
      this.setState({ showInactiveAlert: false });
      this.initIdleTimer();
    };

    return (
      <ErrorBoundary
        fallback="An error has occurred"
        onError={() => this.setState({ isError })}
      >
        <NewVersionCheck />
        <BrowserSupportNotification />
        <StylesProvider jss={jss}>
          <UserProvider value={this.state}>
            <div className={classes.root} lang={language} style={isNoToken ? { backgroundImage: `url(${LoginBg})`, backgroundSize: "cover", backgroundRepeat: "no-repeat", backgroundPosition: "center" } : undefined}>
              <NotificationCenter />
              <Grid container alignItems="stretch" style={{ height: "100%" }} className={`lang-${languageWithoutRegionCode}`}>
                <Grid item xs={12} className={classes.appFrame}>
                  {!isLeftSideNavigation && isNoToken && (
                    <div className={classes.logo}>
                      <Header onlyLogo />
                    </div>
                  )}
                  {!isNoToken && !isLeftSideNavigation && (<Header clinics={clinics} />)}
                  {!isNoToken && isLeftSideNavigation && <LeftSideNavigation history={this.props.history} />}
                  <main
                    id="main-app"
                    style={isFullScreen ? { marginTop: 0, overflow: "hidden" } : { position: isLeftSideNavigation ? "relative" : "initial", overflow: "hidden" }}
                    className={`custom-scrollbar-thin ${classes.content} ${classes.contentShift} ${!isLeftSideNavigation ? classes.topNav : ""}`}
                  >
                    <section className={classes.contentWrapper}>
                      {(localStorage.getItem("access_token") || localStorage.getItem("code_login_token") || localStorage.getItem("class_login_token")) && (
                        <Switch>
                          <Route path="/treatment/v3/:treatmentId?/:studentId?" exact>
                            {(currentUser.isLoading || !currentUser.isVerified) ? (
                              <FullScreenLoading />
                            ) : (
                              <Suspense fallback={<FullScreenLoading />}>
                                <TreatmentComponent />
                              </Suspense>
                            )}
                          </Route>
                          <Redirect
                            from="/treatment/v2/:treatmentId?"
                            to="/treatment/v3/:treatmentId?"
                          />
                          <Route path="/treatment" render={props => <TreatmentRoutes {...props} />} />
                          <Route path="/students" render={props => currentUser.isVerified ? <UsersRoutes {...props} /> : <FullScreenLoading />} />
                          <Route path="/resources" render={props => <ResourcesRoutes {...props} />} />
                          <Route path="/home" render={props => <HomeRoutes {...props} show={currentUser.id} userRole={currentUser.role} />} />
                          {!currentUser.isVerified && <Route path="/login" exact component={LoginPage} />}
                          {!currentUser.isVerified && <Route path="/code" exact component={LoginPage} />}
                          {!currentUser.isVerified && <Route path="/code/:code/" exact component={LoginPage} />}
                          {!currentUser.isVerified && <Route path="/class_code" exact component={LoginPage} />}
                          {!currentUser.isVerified && <Route path="/class_code/:class_code/" exact component={LoginPage} />}
                          <Route path="/login/:provider/success/" exact component={LoginWithOauth} />
                          <Redirect to="/home/" />
                        </Switch>
                      )}
                      {!localStorage.getItem("access_token") && !localStorage.getItem("code_login_token") && !localStorage.getItem("class_login_token") && (
                        <Switch>
                          <Route path="/class_code" exact component={LoginPage} />
                          <Route path="/class_code/:class_code/" exact component={LoginPage} />
                          <Route path="/code" exact component={LoginPage} />
                          <Route path="/code/:code/" exact component={LoginPage} />
                          <Route path="/login" exact component={LoginPage} />
                          <Route path="/login/:provider/success/" exact component={LoginWithOauth} />
                          <Route path="/password/reset/confirm/:uuid/:token" component={PasswordReset} />
                          <Route path="/password" component={PasswordPage} />
                          <Route path="/error" component={ErrorRaise} />
                          <Redirect to="/login/" />
                        </Switch>
                      )}
                    </section>
                  </main>
                </Grid>
              </Grid>
              <footer className="main-footer" />
            </div>
            {showBwAlert && (
              <CommonDialog
                maxWidth="md"
                fullWidth
                open
                cancelAction={() => { this.setState({ showBwAlert: false }); }}
                cancelLabel="Close"
                title={<Trans>NETWORK CONNECTIVITY ISSUES</Trans>}
                content={(
                  <div>
                    <div>
                      <Trans>Your internet speed is slow or interrupted, it will affect your audio/video quality.</Trans>
                    </div>
                    <div>
                      <Trans>Please check your internet connection or try to reload the page.</Trans>
                    </div>
                  </div>
                )}
              />
            )}
            {showInactiveAlert && (
              <CommonDialog
                maxWidth="md"
                open
                okAction={warningConfirmation}
                okLabel={<Trans>Accepted</Trans>}
                style={{ zIndex: 1500 }}
                content={(
                  <div
                    style={{ fontSize: "18px" }}
                  >
                    <Trans>We have not detected activity.</Trans> <br />
                    <Trans>In order to protect out user information, you will be disconnected in</Trans>&nbsp;
                    {warningDuration} &nbsp;
                    <Trans>seconds</Trans>
                    <br />
                    <Trans>Do you want to stay connected?</Trans>
                  </div>
                )}
              />
            )}
          </UserProvider>
        </StylesProvider>
      </ErrorBoundary>
    );
  }
}

App.propTypes = propTypes;

const mapStateToProps = (state) => ({
  currentUser: currentUserSelector(state),
  selectedClinic: state.clinics.selectedClinic,
  clinics: state.entities.clinics,
  isFullScreen: selectIsFullScreen(state),
});

const AppWithRouter = withRouter(
  connect(mapStateToProps, {
    verifyVersion: verifyVersion.request,
    loadUser: loadUser.request,
    loadClinics: loadClinics.request,
    logout: logout.request,
  })(withStyles(styles, { withTheme: true })(App)),
);


const AppContainer = () => (

  <Router basename="/">
    <Switch>
      <Route path="/" match component={AppWithRouter} />
    </Switch>
  </Router>
);

export default AppContainer;
